import React, { useState } from 'react';
import { XMarkIcon, ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

// Helper function to get the correct image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If it's already a full URL
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // If it's a relative path, add the storage prefix
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${apiUrl}/storage/${imagePath}`;
};

const TourGallery = ({ images, title }) => {
  // Debug the incoming images
  console.log('TourGallery received images:', images);
  
  // Ensure images is always an array and filter out null/undefined values
  const imageArray = Array.isArray(images) ? images.filter(img => img) : [];
  
  const [activeIndex, setActiveIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === 0 ? imageArray.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev === imageArray.length - 1 ? 0 : prev + 1));
  };

  const openLightbox = (index) => {
    setActiveIndex(index);
    setShowLightbox(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    document.body.style.overflow = 'auto';
  };

  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showLightbox) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        handlePrevImage(e);
      } else if (e.key === 'ArrowRight') {
        handleNextImage(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox]);

  // If no images, show a placeholder
  if (!imageArray.length) {
    return (
      <div className="bg-gray-900">
        <div className="container-custom py-4">
          <div className="h-[500px] flex items-center justify-center bg-gray-800 rounded-lg">
            <p className="text-white text-xl">No images available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-900">
        <div className="container-custom py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 h-[500px]">
            {/* Main image */}
            <div 
              className="md:col-span-2 md:row-span-2 h-60 md:h-full cursor-pointer relative"
              onClick={() => openLightbox(0)}
            >
              <img 
                src={getImageUrl(imageArray[0])} 
                alt={`${title} - main`} 
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                }}
              />
              <div className="absolute inset-0 bg-black bg-opacity-20 hover:bg-opacity-10 transition-all duration-300 rounded-lg"></div>
            </div>
            
            {/* Secondary images */}
            {imageArray.slice(1, 5).map((image, index) => (
              <div 
                key={index + 1}
                className="h-60 cursor-pointer relative"
                onClick={() => openLightbox(index + 1)}
              >
                <img 
                  src={getImageUrl(image)} 
                  alt={`${title} - ${index + 1}`} 
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                  }}
                />
                <div className="absolute inset-0 bg-black bg-opacity-20 hover:bg-opacity-10 transition-all duration-300 rounded-lg"></div>
                
                {/* Show "View All Photos" on the last visible image */}
                {index === 3 && imageArray.length > 5 && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                    <span className="text-white font-medium">
                      +{imageArray.length - 5} more photos
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <div className="relative w-full max-w-5xl px-4">
            {/* Close button */}
            <button 
              className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all duration-300"
              onClick={closeLightbox}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            
            {/* Navigation buttons */}
            <button 
              className="absolute left-8 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all duration-300"
              onClick={handlePrevImage}
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <button 
              className="absolute right-8 top-1/2 transform -translate-y-1/2 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all duration-300"
              onClick={handleNextImage}
            >
              <ArrowRightIcon className="h-6 w-6" />
            </button>
            
            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 px-3 py-1 rounded-full text-white text-sm">
              {activeIndex + 1} / {imageArray.length}
            </div>
            
            {/* Main image */}
            <img 
              src={getImageUrl(imageArray[activeIndex])} 
              alt={`${title} - ${activeIndex}`} 
              className="w-full h-auto max-h-[80vh] object-contain mx-auto"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/1200x800?text=Image+Not+Available';
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default TourGallery;
